/**
 * The needs-review queue — what the AI moderation pass flagged, and the two decisions a human
 * can make about it.
 *
 * Metadata only. Every row here carries names, timestamps, a divert reason and (for one reason)
 * category codes — and NEVER a word of what the referee actually said. That is not an oversight
 * to be improved on: a queue row carrying an excerpt would be a way around the break-glass
 * chokepoint, so reading the transcript is a second, separate, audited act (BreakGlassPanel).
 *
 * The two decisions:
 *   APPROVE re-drives the pipeline (`needs_review → processing → drafted`). It does NOT send the
 *     referee anything — the PIPELINE sends the "your letter is ready" email once it has drafted.
 *     Saying otherwise in the UI would have staff believing they had emailed someone when they
 *     had not, or vice versa.
 *   REJECT moves it to `rerecord_requested` and emails the referee a fresh link asking for
 *     another take. That email is deliberately generic and NEVER carries the moderation
 *     categories — the referee is not told a machine called their words abusive. The categories
 *     on this screen are for the reviewer's judgement and stop here.
 *
 * Approve is not valid for every reason: `no_key_points` has nothing to ground a letter on and
 * the API answers 409 `nothing_to_draft`. The button is disabled with the reason stated, rather
 * than inviting the click and surfacing a raw 409 after the fact.
 *
 * Oldest first, 25 per page — the API orders and pages it; this screen does not re-sort.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PAGE_SIZE,
  approveReview,
  errorCode,
  fetchReviewQueue,
  rejectReview,
} from '@/lib/admin-api';
import {
  approveBlockedReason,
  canApprove,
  divertReasonCopy,
  formatTimestamp,
  formatWaiting,
  moderationCategoryLabel,
  type NeedsReviewItem,
} from '@/lib/wire';
import { PageHeader } from '@/components/AppShell';
import { ConfirmAction } from '@/components/ConfirmAction';
import { EmptyState, ErrorState, ErrorText, Loading } from '@/components/DataState';
import { Pager } from '@/components/Pager';
import { Badge } from '@/components/ui/badge';
import { BreakGlassPanel } from './BreakGlassPanel';

export function Queue() {
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();

  const queue = useQuery({
    queryKey: ['review-queue', offset],
    queryFn: () => fetchReviewQueue(offset),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['review-queue'] });

  return (
    <>
      <PageHeader title="Needs review">
        References the AI pipeline diverted for a human to look at, oldest first. Metadata only
        &mdash; what was actually said is behind break glass, per reference.
      </PageHeader>

      {queue.isPending && <Loading label="Loading the queue…" />}
      {queue.isError && <ErrorState onRetry={() => queue.refetch()} />}

      {queue.isSuccess &&
        (queue.data.items.length === 0 ? (
          <EmptyState title="Nothing waiting">
            No references are held for review. This is the state we want.
          </EmptyState>
        ) : (
          <>
            <ul className="flex flex-col gap-3">
              {queue.data.items.map((item) => (
                <QueueRow
                  key={item.referenceRequestId}
                  item={item}
                  onDecided={invalidate}
                  isLastOnPage={queue.data.items.length === 1 && offset > 0}
                  onPageEmptied={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
                />
              ))}
            </ul>
            <Pager
              offset={offset}
              total={queue.data.total}
              count={queue.data.items.length}
              onOffsetChange={setOffset}
              noun="reference"
            />
          </>
        ))}
    </>
  );
}

interface QueueRowProps {
  item: NeedsReviewItem;
  onDecided: () => void;
  isLastOnPage: boolean;
  onPageEmptied: () => void;
}

function QueueRow({ item, onDecided, isLastOnPage, onPageEmptied }: QueueRowProps) {
  const [decided, setDecided] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const reason = divertReasonCopy(item.divertReason);
  const approvable = canApprove(item.divertReason);
  const blockedReason = approveBlockedReason(item.divertReason);

  function settle(outcome: string) {
    setDecided(outcome);
    setFailure(null);
    // Stepping back a page when the last row on it is gone keeps the reviewer from landing on an
    // empty page they then have to page out of.
    if (isLastOnPage) onPageEmptied();
    onDecided();
  }

  function fail(caught: unknown) {
    const code = errorCode(caught);
    if (code === 'nothing_to_draft') {
      // Reachable despite the disabled button — someone else may have changed the request, or
      // the divert reason we rendered is stale. Say the true thing rather than "unknown error".
      setFailure('There are no key points to draft from. Ask for another take instead.');
      return;
    }
    if (code?.startsWith('not_in_review:')) {
      setFailure(
        `Someone else already handled this — it is now "${code.slice('not_in_review:'.length)}". Refresh the queue.`,
      );
      return;
    }
    setFailure('That didn’t go through. Nothing changed. Try again in a moment.');
  }

  const approve = useMutation({
    mutationFn: () => approveReview(item.referenceRequestId),
    onSuccess: () => settle('Approved — the pipeline is re-drafting it now.'),
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: () => rejectReview(item.referenceRequestId),
    onSuccess: () => settle('Sent back — the referee has been asked for another take.'),
    onError: fail,
  });

  const pending = approve.isPending || reject.isPending;

  return (
    <li className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-[15px] font-semibold tracking-[-0.01em] text-ink">
            {item.refereeName}{' '}
            <span className="font-normal text-ink-70">for {item.candidateName}</span>
          </h2>
          <p className="mt-1 font-body text-[11.5px] text-ink-70">
            Requested {formatTimestamp(item.requestedAt)} &middot; in review for{' '}
            {formatWaiting(item.enteredReviewAt)}
          </p>
        </div>
        <Badge tone={item.divertReason === 'moderation_flagged' ? 'coral' : 'neutral'}>
          {reason.label}
        </Badge>
      </div>

      <p className="mt-2.5 max-w-[76ch] font-body text-[12.5px] leading-relaxed text-ink-70">
        {reason.detail}
      </p>

      {item.moderationCategories.length > 0 && (
        <div className="mt-2.5">
          <h3 className="font-display text-[11px] font-medium uppercase tracking-wide text-ink-quiet">
            Flagged categories
          </h3>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {item.moderationCategories.map((category) => (
              <li key={category}>
                <Badge tone="coral">{moderationCategoryLabel(category)}</Badge>
              </li>
            ))}
          </ul>
          {/* Said once, on the screen where someone might otherwise assume the referee has been
              told what a model thought of them. */}
          <p className="mt-1.5 font-body text-[11px] leading-relaxed text-ink-70">
            For your judgement only. The referee is never told these, whatever you decide.
          </p>
        </div>
      )}

      {decided ? (
        <p
          role="status"
          className="mt-3 rounded-md border border-sage-deep/30 bg-sage-tint/50 px-3.5 py-2.5 font-body text-[12.5px] font-medium text-ink"
        >
          {decided}
        </p>
      ) : (
        <>
          {failure && <ErrorText className="mt-3">{failure}</ErrorText>}

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
            <ConfirmAction
              label="Approve"
              triggerVariant="default"
              disabled={!approvable || pending}
              disabledReason={blockedReason ?? undefined}
              title="Approve and re-draft this reference?"
              confirmLabel="Yes — approve"
              pending={approve.isPending}
              onConfirm={() => approve.mutate()}
              className="sm:max-w-[34rem]"
            >
              <p>
                The pipeline picks this up again and drafts the letter. When the draft is ready,{' '}
                <strong>the pipeline</strong> emails {item.refereeName} &mdash; approving does not
                send anything itself.
              </p>
              <p className="mt-1.5">This is recorded against your staff id.</p>
            </ConfirmAction>

            <ConfirmAction
              label="Ask for another take"
              triggerVariant="coral"
              disabled={pending}
              title={`Send this back to ${item.refereeName}?`}
              confirmLabel="Yes — send it back"
              confirmVariant="coral"
              pending={reject.isPending}
              onConfirm={() => reject.mutate()}
              className="sm:max-w-[34rem]"
            >
              <p>
                {item.refereeName} gets an email with a fresh link, asking for another take. It is
                deliberately gentle and says nothing about moderation &mdash; they will not be told
                what was flagged, or that anything was.
              </p>
              <p className="mt-1.5">This is recorded against your staff id.</p>
            </ConfirmAction>
          </div>

          {/* A separate, deliberate act — never fired by rendering this row. */}
          <BreakGlassPanel
            referenceRequestId={item.referenceRequestId}
            refereeName={item.refereeName}
          />
        </>
      )}
    </li>
  );
}
